import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DRAFT_POLYGON_ID, usePolygonEditor } from "../usePolygonEditor";
import {
  FLOOR_PLAN_ROOM_ACTIVE_RGB,
  FLOOR_PLAN_ROOM_RGB,
  getNextRoomName,
} from "../../utils/rooms";
import type { PolygonOverlayEntry } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/PolygonsLayer/types";
import type { PersistPolygonGeometryArgs } from "../usePolygonEditor";
import type { PolygonPoint } from "../../utils/polygonPointMutators";
import type {
  PendingRoom,
  RenamePendingRoomArgs,
  UseRoomsCreationArgs,
  UseRoomsCreationResult,
} from "./types";

export const useRoomsCreation = ({
  isActive,
  step,
  pageRooms,
  pageId,
  pageIndex,
  isSaving,
  createRooms,
  setStep,
  stop,
  floorPlanPanelHandlers,
}: UseRoomsCreationArgs): UseRoomsCreationResult => {
  const { t } = useTranslation();

  // Counts down so a pending id can never match a saved room id.
  const nextPendingIdRef = useRef(-1);

  const [pendingRooms, setPendingRooms] = useState<PendingRoom[]>([]);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const persistGeometry = useCallback(
    ({ id, points }: PersistPolygonGeometryArgs) => {
      setPendingRooms((previous) =>
        previous.map((room) => (room.id === id ? { ...room, points } : room)),
      );
      return Promise.resolve();
    },
    [],
  );

  const takeOverDraft = useCallback(
    (points: PolygonPoint[]) => {
      const id = nextPendingIdRef.current;
      nextPendingIdRef.current -= 1;
      setPendingRooms((previous) => [
        ...previous,
        {
          id,
          name: getNextRoomName({
            existingNames: [...pageRooms, ...previous].map((room) => room.name),
            formatName: (index) =>
              t("smartSurveyor.rooms.defaultName", { index }),
          }),
          points,
        },
      ]);
      return id;
    },
    [pageRooms, t],
  );

  const {
    draftPoints,
    selectedTarget,
    selectedPointId,
    hoverPosition,
    modal,
    isEditingActive,
    resolvePoints,
    selectTarget,
    startDrawing,
    finalizeDraft,
    confirmDiscard,
    closeModal,
    onPolygonDeleted,
    handleMapClick,
    handleMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  } = usePolygonEditor({
    isActive,
    step,
    savedPolygons: pendingRooms,
    setStep,
    stop,
    persistGeometry,
    maxPointsMessage: t("smartSurveyor.rooms.maxPointsReached"),
    minPointsMessage: t("smartSurveyor.rooms.minPointsRequired"),
    onDraftFinalized: takeOverDraft,
  });

  const isDrawing = step === "drawing";

  const toggleDrawing = useCallback(() => {
    if (isDrawing) {
      finalizeDraft();
      return;
    }
    startDrawing();
  }, [isDrawing, finalizeDraft, startDrawing]);

  // Picking a room in the list zooms to it; a click on the map does not, the user is already looking at it
  const selectPendingRoom = useCallback(
    (id: number) => {
      selectTarget(String(id));
      const isDeselecting =
        selectedTarget?.type === "saved" && selectedTarget.id === id;
      if (isDeselecting) return;
      const room = pendingRooms.find((candidate) => candidate.id === id);
      if (!room) return;
      floorPlanPanelHandlers?.current?.zoomToElements({
        elements: room.points,
      });
    },
    [selectTarget, selectedTarget, pendingRooms, floorPlanPanelHandlers],
  );

  const renamePendingRoom = useCallback(
    ({ id, name }: RenamePendingRoomArgs) => {
      setPendingRooms((previous) =>
        previous.map((room) => (room.id === id ? { ...room, name } : room)),
      );
    },
    [],
  );

  const removePendingRoom = useCallback(
    (id: number) => {
      setPendingRooms((previous) => previous.filter((room) => room.id !== id));
      onPolygonDeleted(id);
    },
    [onPolygonDeleted],
  );

  const saveAll = useCallback(async () => {
    if (pageId === undefined || pendingRooms.length === 0) return;
    const created = await createRooms({
      pageId,
      pageIndex,
      rooms: pendingRooms.map(({ name, points }) => ({
        name: name.trim(),
        points,
      })),
    });
    if (created === null) return;
    stop();
  }, [pageId, pendingRooms, createRooms, pageIndex, stop]);

  const requestLeave = useCallback(() => {
    const hasUnsavedWork =
      pendingRooms.length > 0 ||
      (draftPoints !== null && draftPoints.length > 0);
    if (!hasUnsavedWork) {
      stop();
      return;
    }
    setIsDiscardModalOpen(true);
  }, [pendingRooms, draftPoints, stop]);

  const confirmLeave = useCallback(() => {
    setIsDiscardModalOpen(false);
    stop();
  }, [stop]);

  const closeDiscardModal = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  // The saved rooms are drawn for orientation only; kept apart from the hover-driven
  // overlay, so they keep their identity and skip re-rendering.
  const lockedEntries = useMemo<PolygonOverlayEntry[]>(
    () =>
      pageRooms.map((room) => ({
        id: String(room.id),
        points: room.points,
        rgb: FLOOR_PLAN_ROOM_RGB,
        label: room.name,
        isSelected: false,
        isDraft: false,
        isLocked: true,
      })),
    [pageRooms],
  );

  const overlay = useMemo(() => {
    const pendingEntries: PolygonOverlayEntry[] = pendingRooms.map((room) => {
      const isSelected =
        selectedTarget?.type === "saved" && selectedTarget.id === room.id;
      return {
        id: String(room.id),
        points: resolvePoints(room),
        rgb: isSelected ? FLOOR_PLAN_ROOM_ACTIVE_RGB : FLOOR_PLAN_ROOM_RGB,
        label: room.name,
        isSelected,
        isDraft: false,
      };
    });

    const draftEntries: PolygonOverlayEntry[] = draftPoints
      ? [
          {
            id: DRAFT_POLYGON_ID,
            points: draftPoints,
            rgb: FLOOR_PLAN_ROOM_ACTIVE_RGB,
            isSelected: selectedTarget?.type === "draft",
            isDraft: true,
          },
        ]
      : [];

    return {
      polygons: [...lockedEntries, ...pendingEntries, ...draftEntries],
      hoverPosition,
      isDrawing,
      isInteractive: step === "list",
      isEditable: isEditingActive,
      selectedPointId,
      onPolygonClick: selectTarget,
      onNodeMouseDown: handleNodeMouseDown,
      onSegmentMouseDown: handleSegmentMouseDown,
    };
  }, [
    lockedEntries,
    pendingRooms,
    selectedTarget,
    resolvePoints,
    draftPoints,
    hoverPosition,
    isDrawing,
    step,
    isEditingActive,
    selectedPointId,
    selectTarget,
    handleNodeMouseDown,
    handleSegmentMouseDown,
  ]);

  // Leaving drops the session, like the polygon editor drops its own state.
  useEffect(() => {
    if (isActive) return;
    setPendingRooms([]);
    setIsDiscardModalOpen(false);
  }, [isActive]);

  return {
    state: {
      pendingRooms,
      isDrawing,
      isSaving,
      selectedTarget,
      draftModal: modal,
      isDiscardModalOpen,
      toggleDrawing,
      selectPendingRoom,
      renamePendingRoom,
      removePendingRoom,
      saveAll,
      requestLeave,
      confirmLeave,
      closeDiscardModal,
      confirmDiscardDraft: confirmDiscard,
      closeDraftModal: closeModal,
    },
    overlay,
    isEditingActive,
    handleMapClick,
    handleMapMouseMove,
  };
};
